import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, forkJoin, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { OnboardingMockupItem, OnboardingStep } from '@pages/shared/models/onboarding.types';
import { SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { ONBOARDING_STEPS } from '@app/configuration.service';
import { Router } from '@angular/router';
import { BazisModalService } from '@bazis/shared/services/modal.service';
import { ModalEndOnboardingComponent } from '@pages/shared/components/onboarding/modal-end-onboarding.component';
import { UntilDestroy } from '@ngneat/until-destroy';

// добавить метод скрытия степа, скрытия всего онбординга (с сохранением в localstorage статуса)

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class OnboardingService {
    mockupDataLinks: OnboardingMockupItem[] = [
        {
            id: 'logistics.geozone',
            jsonFile: '/assets/onboarding/geozone.json',
        },
        {
            id: 'logistics.warehouse',
            jsonFile: '/assets/onboarding/warehouse.json',
        },
        {
            id: 'logistics.staff',
            jsonFile: '/assets/onboarding/courier.json',
        },
        {
            id: 'logistics.vehicle',
            jsonFile: '/assets/onboarding/vehicle.json',
        },
        {
            id: 'logistics.depot',
            jsonFile: '/assets/onboarding/vehicle-depot.json',
        },
        {
            id: 'logistics.cargo_type',
            jsonFile: '/assets/onboarding/cargo.json',
        },
        {
            id: 'order.order',
            jsonFile: '/assets/onboarding/order.json',
        },
        {
            id: 'planning.planning',
            jsonFile: '/assets/onboarding/planning.json',
        },
        {
            id: 'monitoring',
            jsonFile: '/assets/onboarding/monitoring.json',
        },
    ];

    showOnboarding$ = new TemplateObservable(false);

    steps: OnboardingStep[] = ONBOARDING_STEPS.map((step) => {
        const onboardingStep: OnboardingStep = step as OnboardingStep;
        return onboardingStep;
    });

    indexCurrentStep$ = new TemplateObservable(0);

    indexCurrentStepBubble = 0;

    constructor(
        protected http: HttpClient,
        protected router: Router,
        protected modalService: BazisModalService,
    ) {}

    getMockupData$(): Observable<{ [key: string]: any }> {
        const observables: { [key: string]: any } = {};
        this.mockupDataLinks.forEach((mockupItem) => {
            observables[mockupItem.id] = this.http.get(mockupItem.jsonFile);
        });

        return forkJoin(observables).pipe(
            tap((data) => {
                Object.entries(data).forEach(([key, value]) => {
                    // Используем id в качестве ключа для localStorage
                    // Преобразуем значение в JSON-строку
                    localStorage.setItem('onboarding.' + key, JSON.stringify(value));
                });
            }),
            catchError((e) => {
                console.warn('loadOnboarding err', e);
                return of(e);
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    init() {
        this.getMockupData$().subscribe();
        this.showOnboarding$.$.subscribe((showOnboarding) => {
            if (!showOnboarding) {
                const cleanUrl = this.router.url.split('?')[0];

                switch (cleanUrl) {
                    case '/routes/plannings/onboarding/edit':
                        this.router.navigateByUrl('/routes/plannings/new/edit');
                        break;
                    case '/routes/plannings/onboarding':
                        this.router.navigateByUrl('/routes/plannings/active');
                        break;
                    case '/routes/plannings/onboarding/edit/orders/onboarding/edit':
                        this.router.navigateByUrl('/routes/plannings/new/edit/orders/new/edit');
                        break;

                    default:
                        break;
                }
            }
        });

        this.show();
    }

    show() {
        this.showOnboarding$.set(true);
    }

    hide() {
        this.showOnboarding$.set(false);
        this.indexCurrentStep$.set(0);
        this.indexCurrentStepBubble = 0;
    }

    navigate(indexCurrentStep: number, type: 'prev' | 'next') {
        const newIndexStep =
            type === 'next' ? indexCurrentStep + 1 : this.getNewIndexStep(indexCurrentStep);

        if (indexCurrentStep + 1 === this.steps.length && type === 'next') {
            this.modalService.create({
                component: ModalEndOnboardingComponent,
                backdropClose: false,
                hasCloseIcon: false,
                cssClass: 'tms-modal_onboarding',
            });
            this.router.navigateByUrl('/monitoring/routes');
            this.indexCurrentStep$.set(indexCurrentStep + 1);
            return;
        }

        const currentStep = this.steps[indexCurrentStep];
        const url = this.steps[newIndexStep].url;

        if (currentStep.url !== url) {
            this.router
                .navigateByUrl(url)
                .then((success: boolean) => {
                    if (success) {
                        console.log('✅ Навигация успешно завершена.');

                        // необходимо время на прогрузку компонента, чтобы появился таргет элемент
                        setTimeout(() => {
                            this.setNextIndex(newIndexStep, currentStep, type);
                        }, 100);
                    } else {
                        console.log('❌ Навигация была отменена.');
                    }
                })
                .catch((error: any) => {
                    console.error('❗️ Произошла ошибка во время навигации:', error);
                });
        } else {
            this.setNextIndex(newIndexStep, currentStep, type);
        }

        if (this.steps[newIndexStep].withoutBubble) {
            setTimeout(() => {
                this.navigate(newIndexStep, type);
            }, this.steps[newIndexStep].animationDelay);
        }
    }

    getNewIndexStep(indexCurrentStep: number): number {
        const newIndexStep = this.steps[indexCurrentStep - 1].withoutBubble
            ? this.getNewIndexStep(indexCurrentStep - 1)
            : indexCurrentStep - 1;

        return indexCurrentStep - 1;
    }

    setNextIndex(newIndexStep, currentStep, type) {
        this.indexCurrentStep$.set(newIndexStep);
        if (!currentStep.withoutBubble) {
            this.indexCurrentStepBubble =
                type === 'prev' ? this.indexCurrentStepBubble - 1 : this.indexCurrentStepBubble + 1;
        }
    }
}
